import React from 'react';
import ReactFlow, { MiniMap, Controls, Background, Position, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import './App.css'; // Assuming some shared styles might be in App.css

const turintechNodes = [
  // Business Use Case
  { id: 'tt-buc', data: { label: '🎯 Business Use Case Example' }, position: { x: 0, y: 250 }, sourcePosition: Position.Right, targetPosition: Position.Left },

  // Data Ingestion Group
  { id: 'tt-db', data: { label: '🗄️ Read Database' }, position: { x: 250, y: 50 }, sourcePosition: Position.Right, targetPosition: Position.Left },
  { id: 'tt-csv', data: { label: '📄 Read CSV' }, position: { x: 250, y: 150 }, sourcePosition: Position.Right, targetPosition: Position.Left },
  { id: 'tt-excel', data: { label: '📊 Read Excel' }, position: { x: 250, y: 250 }, sourcePosition: Position.Right, targetPosition: Position.Left },

  // Data Preparation Group
  { id: 'tt-combine', data: { label: '🔗 Combine Data' }, position: { x: 500, y: 150 }, sourcePosition: Position.Bottom, targetPosition: Position.Left },
  { id: 'tt-artemis-fe-early', data: { label: '⚙️ Early Feature Engineering (Artemis-assisted)' }, position: { x: 500, y: 250 }, sourcePosition: Position.Bottom, targetPosition: Position.Top, style: { backgroundColor: 'rgba(255, 200, 200, 0.5)' } },
  { id: 'tt-combine-csv', data: { label: '📄 Merged & Early Engineered Data' }, position: { x: 500, y: 350 }, sourcePosition: Position.Bottom, targetPosition: Position.Top },
  { id: 'tt-clean', data: { label: '🧹 Data Cleaning' }, position: { x: 500, y: 450 }, sourcePosition: Position.Bottom, targetPosition: Position.Top, style: { backgroundColor: 'rgba(200, 255, 200, 0.5)' } },
  { id: 'tt-fe', data: { label: '🛠️ Feature Engineering (EvoML & Artemis)' }, position: { x: 500, y: 550 }, sourcePosition: Position.Right, targetPosition: Position.Top, style: { backgroundColor: 'rgba(220, 200, 255, 0.5)' } },

  // Model Training & Evaluation Group
  { id: 'tt-split', data: { label: '🧪 Train-Test Split' }, position: { x: 750, y: 150 }, sourcePosition: Position.Bottom, targetPosition: Position.Left, style: { backgroundColor: 'rgba(200, 255, 200, 0.5)' } },
  { id: 'tt-select', data: { label: '📊 Model Selection' }, position: { x: 750, y: 250 }, sourcePosition: Position.Bottom, targetPosition: Position.Top, style: { backgroundColor: 'rgba(200, 255, 200, 0.5)' } },
  { id: 'tt-train', data: { label: '🤖 Training' }, position: { x: 750, y: 350 }, sourcePosition: Position.Bottom, targetPosition: Position.Top, style: { backgroundColor: 'rgba(200, 255, 200, 0.5)' } },
  { id: 'tt-eval', data: { label: '📈 Evaluation' }, position: { x: 750, y: 450 }, sourcePosition: Position.Right, targetPosition: Position.Top, style: { backgroundColor: 'rgba(200, 255, 200, 0.5)' } },

  // Deployment Group
  { id: 'tt-deploy', data: { label: '🚀 Deploy' }, position: { x: 1000, y: 250 }, sourcePosition: Position.Bottom, targetPosition: Position.Left, style: { backgroundColor: 'rgba(200, 255, 200, 0.5)' } },
  { id: 'tt-app', data: { label: '📱 Interactive App' }, position: { x: 1000, y: 350 }, sourcePosition: Position.Right, targetPosition: Position.Top, style: { backgroundColor: 'rgba(255, 200, 200, 0.5)' } },

  // Legend Boxes
  {
    id: 'evoml-legend',
    type: 'output',
    data: {
      label: (
        <>
          <div style={{ fontWeight: 'bold', marginBottom: '5px', textAlign:'center', color: '#2E7D32' }}>EvoML - AutoML Platform & MLOps Automation</div>
          <ul style={{ listStylePosition: 'inside', paddingLeft: '0px', textAlign: 'left', fontSize: '11px', marginBottom: '10px' }}>
            <li>Automated Data Cleaning (missing values, etc.)</li>
            <li>Advanced Feature Engineering (conversion, OHE, PCA, math/trig generation)</li>
            <li>Comprehensive Model Selection</li>
            <li>Genetic Algorithm-based Hyperparameter Tuning</li>
            <li>Generates Full Python Pipeline Code</li>
            <li>MLOps Ready (MLflow, Docker integrations)</li>
          </ul>
          <div style={{ fontWeight: 'bold', marginTop:'10px', marginBottom: '5px', textAlign:'center', color: '#1B5E20' }}>evoml-python Client:</div>
          <ul style={{ listStylePosition: 'inside', paddingLeft: '0px', textAlign: 'left', fontSize: '11px' }}>
            <li>Programmatic control & intervention in the EvoML pipeline</li>
            <li>Script custom model selections & ensembling strategies</li>
            <li>Execute targeted hyperparameter optimization routines</li>
            <li>Automate batch trial execution & comparative analysis</li>
            <li>Build and manage server-side automated model (re)training pipelines</li>
            <li>Seamlessly integrate EvoML capabilities into existing Python workflows</li>
          </ul>
        </>
      ),
    },
    position: { x: 50, y: 680 },
    style: { backgroundColor: 'rgba(200, 255, 200, 0.8)', width: 320, padding:'10px', border: '2px solid #388E3C', borderRadius: '8px' },
    selectable: false,
    draggable: false,
  },
  {
    id: 'artemis-legend',
    type: 'output',
    data: {
      label: (
        <>
          <div style={{ fontWeight: 'bold', marginBottom: '5px', textAlign:'center', color: '#C62828' }}>Artemis - AI Code Evolution & App Generation</div>
          <ul style={{ listStylePosition: 'inside', paddingLeft: '0px', textAlign: 'left', fontSize: '11px' }}>
            <li>Intelligent Code Refactoring & Optimization</li>
            <li>Evolves Feature Engineering Code (e.g., custom indicators based on model performance for *Early FE* or *Main FE*)</li>
            <li>Automated Generation of Personalized Dashboards (e.g., Streamlit)</li>
            <li>Context-aware Visualization for Analysts</li>
          </ul>
        </>
      ),
    },
    position: { x: 400, y: 680 },
    style: { backgroundColor: 'rgba(255, 200, 200, 0.8)', width: 300, padding:'10px', border: '2px solid #D32F2F', borderRadius: '8px' },
    selectable: false,
    draggable: false,
  },
];

const turintechEdges = [
  // Business Use Case to Data Ingestion
  { id: 'tt-e-buc-db', source: 'tt-buc', target: 'tt-db', animated: true },
  { id: 'tt-e-buc-csv', source: 'tt-buc', target: 'tt-csv', animated: true },
  { id: 'tt-e-buc-excel', source: 'tt-buc', target: 'tt-excel', animated: true },

  // Data Ingestion to Combine Data
  { id: 'tt-e-db-combine', source: 'tt-db', target: 'tt-combine', animated: true },
  { id: 'tt-e-csv-combine', source: 'tt-csv', target: 'tt-combine', animated: true },
  { id: 'tt-e-excel-combine', source: 'tt-excel', target: 'tt-combine', animated: true },

  // Data Preparation Flow
  { id: 'tt-e-combine-artemisfe', source: 'tt-combine', target: 'tt-artemis-fe-early', animated: true },
  { id: 'tt-e-artemisfe-combinecsv', source: 'tt-artemis-fe-early', target: 'tt-combine-csv', animated: true },
  { id: 'tt-e-combinecsv-clean', source: 'tt-combine-csv', target: 'tt-clean', animated: true },
  { id: 'tt-e-clean-fe', source: 'tt-clean', target: 'tt-fe', animated: true },

  // Data Prep to Model Training
  { id: 'tt-e-fe-split', source: 'tt-fe', target: 'tt-split', animated: true },

  // Model Training & Evaluation Flow
  { id: 'tt-e-split-select', source: 'tt-split', target: 'tt-select', animated: true },
  { id: 'tt-e-select-train', source: 'tt-select', target: 'tt-train', animated: true },
  { id: 'tt-e-train-eval', source: 'tt-train', target: 'tt-eval', animated: true },

  // Model Eval to Deployment
  { id: 'tt-e-eval-deploy', source: 'tt-eval', target: 'tt-deploy', animated: true },

  // Deployment Flow
  { id: 'tt-e-deploy-app', source: 'tt-deploy', target: 'tt-app', animated: true },
];

const TurintechWorkflow = ({ title }) => (
  <ReactFlowProvider>
    <div style={{ height: 'calc(100vh - 100px)', width: '100%', pointerEvents: 'all' }}>
      <h2>{title}</h2>
      <ReactFlow
        nodes={turintechNodes}
        edges={turintechEdges}
        fitView
        nodesDraggable={true}
        elementsSelectable={true}
      >
        <MiniMap pannable zoomable />
        <Controls />
        <Background />
      </ReactFlow>
    </div>
  </ReactFlowProvider>
);

export default TurintechWorkflow; 